import { useEffect, useState, type CSSProperties } from "react";
import { Link, useParams } from "react-router-dom";
import {
  startAssessment,
  submitAssessment,
  type StartAssessmentResponse,
  type SubmitAssessmentResponse,
} from "../api";
import { Card, Shell } from "../ui";

type State =
  | { phase: "loading" }
  | { phase: "error"; message: string }
  | { phase: "answering"; attempt: StartAssessmentResponse }
  | { phase: "submitting"; attempt: StartAssessmentResponse }
  | { phase: "result"; result: SubmitAssessmentResponse };

const btnStyle: CSSProperties = {
  padding: "0.5rem 1.1rem",
  borderRadius: 8,
  border: "none",
  background: "#1a1a2e",
  color: "#fff",
  fontSize: "0.9rem",
  cursor: "pointer",
};

export function AssessmentView() {
  const { trackId } = useParams<{ trackId: string }>();
  const [state, setState] = useState<State>({ phase: "loading" });
  const [answers, setAnswers] = useState<Record<string, string[]>>({});

  function loadAttempt() {
    if (!trackId) return;
    setState({ phase: "loading" });
    setAnswers({});
    startAssessment(trackId)
      .then((attempt) => setState({ phase: "answering", attempt }))
      .catch((e: unknown) =>
        setState({ phase: "error", message: e instanceof Error ? e.message : String(e) }),
      );
  }

  useEffect(loadAttempt, [trackId]);

  function toggleAnswer(questionId: string, optionId: string, multi: boolean) {
    setAnswers((prev) => {
      const current = prev[questionId] ?? [];
      if (multi) {
        const next = current.includes(optionId)
          ? current.filter((id) => id !== optionId)
          : [...current, optionId];
        return { ...prev, [questionId]: next };
      }
      return { ...prev, [questionId]: [optionId] };
    });
  }

  function submit() {
    if (state.phase !== "answering") return;
    const attempt = state.attempt;
    setState({ phase: "submitting", attempt });
    const responses = attempt.questions.map((q) => ({
      questionId: q.id,
      selectedOptionIds: answers[q.id] ?? [],
    }));
    submitAssessment(attempt.attemptToken, responses)
      .then((result) => setState({ phase: "result", result }))
      .catch((e: unknown) =>
        setState({ phase: "error", message: e instanceof Error ? e.message : String(e) }),
      );
  }

  if (state.phase === "loading") return <Shell title="Loading…">{null}</Shell>;
  if (state.phase === "error")
    return (
      <Shell title="Assessment" nav={<Link to="/">← My Learning</Link>}>
        <Card>{state.message}</Card>
      </Shell>
    );

  if (state.phase === "result") {
    const r = state.result;
    return (
      <Shell
        title="Assessment result"
        subtitle={`${r.scorePercent}% — ${r.outcome === "PASS" ? "Passed" : "Not yet passed"} (attempt ${r.attemptNumber} of ${r.maxAttempts})`}
        nav={<Link to="/">← My Learning</Link>}
      >
        <Card>
          <p
            style={{
              margin: 0,
              fontSize: "1.1rem",
              color: r.outcome === "PASS" ? "#0a7d33" : "#b00020",
            }}
          >
            {r.outcome === "PASS" ? "You passed. 🎉" : "You did not reach the pass mark this time."}
          </p>
          {r.outcome === "FAIL" && !r.remediationRequired && (
            <p style={{ color: "#555" }}>
              You have {r.maxAttempts - r.attemptNumber} attempt(s) left in this cycle.
            </p>
          )}
          {r.remediationRequired && (
            <p style={{ color: "#555" }}>
              You've used all your attempts. Please review the learning material again, then come
              back here to start a new attempt cycle.
            </p>
          )}
        </Card>

        {r.responses.map((resp, i) => (
          <Card key={resp.questionId}>
            <strong style={{ color: resp.correct ? "#0a7d33" : "#b00020" }}>
              Q{i + 1}: {resp.correct ? "Correct" : "Incorrect"}
            </strong>
            {resp.explanation && (
              <p style={{ color: "#555", fontSize: "0.9rem", marginBottom: 0 }}>
                {resp.explanation}
              </p>
            )}
          </Card>
        ))}

        {r.outcome === "FAIL" && (
          <p style={{ marginTop: "1.5rem" }}>
            <button type="button" style={btnStyle} onClick={loadAttempt}>
              Try again
            </button>
          </p>
        )}
      </Shell>
    );
  }

  const attempt = state.attempt;
  const allAnswered = attempt.questions.every((q) => (answers[q.id] ?? []).length > 0);

  return (
    <Shell
      title="Assessment"
      subtitle={`Attempt ${attempt.attemptNumber} of ${attempt.maxAttempts} · ${attempt.questions.length} questions`}
    >
      {attempt.questions.map((q, i) => {
        const multi = q.type === "MULTI_SELECT";
        return (
          <Card key={q.id}>
            <p style={{ marginTop: 0, fontWeight: 600 }}>
              {i + 1}. {q.stem}
            </p>
            {multi && (
              <p
                style={{
                  marginTop: "-0.5rem",
                  marginBottom: "0.5rem",
                  color: "#666",
                  fontSize: "0.85rem",
                }}
              >
                Select all that apply
              </p>
            )}
            {q.options.map((opt) => (
              <label key={opt.id} style={{ display: "block", padding: "0.25rem 0" }}>
                <input
                  type={multi ? "checkbox" : "radio"}
                  name={q.id}
                  checked={(answers[q.id] ?? []).includes(opt.id)}
                  onChange={() => toggleAnswer(q.id, opt.id, multi)}
                />{" "}
                {opt.text}
              </label>
            ))}
          </Card>
        );
      })}
      <p style={{ marginTop: "1.5rem" }}>
        <button
          type="button"
          style={{ ...btnStyle, opacity: state.phase === "submitting" ? 0.6 : 1 }}
          disabled={!allAnswered || state.phase === "submitting"}
          onClick={submit}
        >
          {state.phase === "submitting" ? "Submitting…" : "Submit"}
        </button>
        {!allAnswered && (
          <span style={{ marginLeft: "0.75rem", color: "#888", fontSize: "0.85rem" }}>
            Answer every question to submit.
          </span>
        )}
      </p>
    </Shell>
  );
}
